// The audio history's reading of an input's samples (internal/node/audiohist.go):
// per channel the loudest sample and the quietest 100 ms of each second (or
// minute), the samples at full scale, and the loudness per stereo pair. A
// list leaves its silent tail out: a missing channel is -100 (silent).
import type { Sample } from './api'

// silence: below this a cell is blank, and a channel that drops below it
// while sounding is a dropout
export const silentDB = -60

export const dbAt = (v: number[] | undefined, i: number): number => v?.[i] ?? -100

// the live meters' bands (Meters.vue): red above -3, green down to -18,
// amber below, blank under silentDB
export type Band = 'hot' | 'good' | 'low' | 'silent'
export function band(db: number): Band {
    return db > -3 ? 'hot' : db > -18 ? 'good' : db > silentDB ? 'low' : 'silent'
}

// strength within a band (0.45..1), so a level change shows inside a colour
export function bandAlpha(db: number): number {
    const [lo, hi] = db > -3 ? [-3, 0] : db > -18 ? [-18, -3] : [silentDB, -18]
    return 0.45 + 0.55 * Math.min(1, Math.max(0, (db - lo) / (hi - lo)))
}

// measured: the samples carry the audio history (an older node, an input
// without a capture: none)
export const measured = (samples: Sample[]): boolean => samples.some((s) => s.peak)

// channels to draw: up to the last that had sound in the window, at least a
// stereo pair
export function channelsShown(samples: Sample[]): number {
    let n = 0
    for (const s of samples)
        for (let c = (s.peak?.length ?? 0) - 1; c >= n; c--)
            if (s.peak![c]! > silentDB) {
                n = c + 1
                break
            }
    return Math.max(n, 2)
}

// pairs with a loudness reading in the window (0: channels 1+2)
export function pairsShown(samples: Sample[]): number[] {
    const out = new Set<number>()
    for (const s of samples) s.lufs?.forEach((l, p) => l > -100 && out.add(p))
    return [...out].sort((a, b) => a - b)
}

export type Mark = { i: number; c: number; kind: 'clip' | 'dropout' }

// marks: clipping wherever a channel had samples at full scale; a dropout
// where a sounding channel's quietest 100 ms fell to silence, inside a loud
// second or as its sound stopped (a channel silent all along is not one)
export function marks(samples: Sample[], channels: number): Mark[] {
    const out: Mark[] = []
    samples.forEach((s, i) => {
        if (!s.peak) return
        const prev = i > 0 ? samples[i - 1] : undefined
        for (let c = 0; c < channels; c++) {
            if ((s.clips?.[c] ?? 0) > 0) out.push({ i, c, kind: 'clip' })
            const sounding = dbAt(s.peak, c) > silentDB || (prev?.peak !== undefined && dbAt(prev.peak, c) > silentDB)
            if (sounding && dbAt(s.floor, c) <= silentDB) out.push({ i, c, kind: 'dropout' })
        }
    })
    return out
}

// channels count from 1, as the meters above them (Meters.vue) and every
// channel picker
export const pairLabel = (p: number) => `${2 * p + 1}–${2 * p + 2}`

// stepAt is the time sample i stands for: 60 s for a minute (stamped on
// the minute, a neighbour a minute or more away: the last minute before
// the one-second tail has seconds after it), else a second; a window mixes
// both, its last minutes at one second
export function stepAt(samples: Sample[], i: number): number {
    const t = samples[i]!.t
    const far = Math.max(samples[i + 1] ? samples[i + 1]!.t - t : 0, i > 0 ? t - samples[i - 1]!.t : 0)
    return t % 60 === 0 && far >= 60 ? 60 : 1
}

// cellEnd is where sample i's cell ends (unix s): at the next sample, but
// no further than its own step past it (a gap in the data stays a gap)
export function cellEnd(samples: Sample[], i: number): number {
    const t = samples[i]!.t
    return Math.min(samples[i + 1]?.t ?? Infinity, t + stepAt(samples, i))
}

export const fmtDB = (db: number) => (db <= -100 ? 'silent' : db.toFixed(Number.isInteger(db) ? 0 : 1))
